import { StyleSheet, Platform, Linking } from 'react-native';
import { Text, Button, Surface, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import { useRouter } from 'expo-router';
import Constants from 'expo-constants';

export default function DownloadScreen() {
  const { t } = useTranslation();
  const theme = useTheme();
  const router = useRouter();
  const version = Constants.expoConfig?.version || '1.0.1';
  const apiBaseUrl = Constants.expoConfig?.extra?.API_BASE_URL || 'https://api.alldrivers.pt';

  const handleDownload = async () => {
    const apkUrl = `${apiBaseUrl}/api/download/apk/alldrivers-staging.apk/`;
    if (Platform.OS === 'web') {
      window.open(apkUrl, '_blank');
    } else {
      await Linking.openURL(apkUrl);
    }
  };

  return (
    <Surface style={[styles.container, { backgroundColor: theme.colors.background }]} elevation={0}>
      <Surface style={[styles.card, { backgroundColor: theme.colors.surface }]} elevation={4}>
        <Animatable.View 
          animation="fadeIn" 
          duration={1000} 
          style={[styles.iconContainer, { backgroundColor: theme.colors.surface }]}
        >
          <MaterialCommunityIcons 
            name="android" 
            size={100} 
            color="#3DDC84" 
          />
        </Animatable.View>

        <Text variant="headlineMedium" style={[styles.title, { color: theme.colors.onSurface }]}>
          {t('download.downloadTitle')}
        </Text>

        <Text variant="bodyLarge" style={[styles.description, { color: theme.colors.onSurfaceVariant }]}>
          {t('download.downloadDescription')}
        </Text>

        <Surface style={[styles.featuresContainer, { backgroundColor: 'transparent' }]} elevation={0}>
          <FeatureItem 
            icon="clock-outline" 
            text={t('download.feature1')} 
            theme={theme}
          />
          <FeatureItem 
            icon="map-marker" 
            text={t('download.feature2')} 
            theme={theme}
          />
          <FeatureItem 
            icon="file-document" 
            text={t('download.feature3')} 
            theme={theme}
          />
        </Surface>

        <Button
          mode="contained"
          onPress={handleDownload}
          style={styles.downloadButton}
          icon="download"
          contentStyle={styles.buttonContent}
          buttonColor={theme.colors.primary}
          textColor={theme.colors.onPrimary}
        >
          {t('download.downloadButton')}
        </Button>

        <Text variant="bodySmall" style={[styles.version, { color: theme.colors.onSurfaceVariant }]}>
          {t('download.version')} {version}
        </Text>

        <Text variant="bodySmall" style={[styles.note, { color: theme.colors.onSurfaceVariant }]}>
          {t('download.downloadNote')}
        </Text>
      </Surface>
    </Surface>
  );
}

const FeatureItem = ({ icon, text, theme }: { icon: keyof typeof MaterialCommunityIcons.glyphMap; text: string; theme: any }) => (
  <Surface style={[styles.featureItem, { backgroundColor: 'transparent' }]} elevation={0}>
    <MaterialCommunityIcons name={icon} size={28} color={theme.colors.primary} />
    <Text variant="bodyMedium" style={[styles.featureText, { color: theme.colors.onSurface }]}>{text}</Text>
  </Surface>
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  card: {
    padding: 32,
    borderRadius: 16,
    marginTop: 20,
    alignItems: 'center',
  },
  iconContainer: {
    marginBottom: 24,
    padding: 16,
    borderRadius: 50,
  },
  title: {
    marginBottom: 16,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  description: {
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 24,
  },
  featuresContainer: {
    width: '100%',
    marginBottom: 40,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  featureText: {
    marginLeft: 16,
    fontSize: 16,
    fontWeight: '500',
  },
  downloadButton: {
    width: '100%',
  },
  buttonContent: {
    paddingVertical: 8,
  },
  version: {
    marginTop: 20,
    fontWeight: '500',
  },
  note: {
    marginTop: 12,
    textAlign: 'center',
    fontStyle: 'italic',
    lineHeight: 20,
  },
}); 